<template lang="pug">
form(@submit.prevent="submit" name="episode-form" novalidate)
  .modal-card
    header.modal-card-head
      p.modal-card-title(v-once) {{ title }}
    section.modal-card-body.modal-tabs
      b-tabs(
        v-model="activeTab"
        position="is-centered"
        expanded
      )
        b-tab-item(label="Media")
          media-tab
        b-tab-item(label="Metadata")
          metadata-tab
        b-tab-item(label="Cover Art")
          cover-art-tab(
            v-model="artwork"
            :default-image="defaultImage"
            @input="a => artwork = a"
          )
    footer.modal-card-foot
      button.button(
        type="submit"
        :class="{ 'is-success': episode.is_published, 'is-primary': !episode.is_published, 'is-loading': settings.submitting }"
      ) {{ saveBtnLabel }}
      a.button.is-danger(v-if="!isNew" @click="confirmDelete") Delete
</template>

<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';
import moment from 'moment';
import CoverArtTab from '@/components/form/episode/CoverArtTab';
import MediaTab from '@/components/form/episode/MediaTab';
import MetadataTab from '@/components/form/episode/MetadataTab';
import * as actions from '@/store/types/actions';
import * as getters from '@/store/types/getters';
import * as mutations from '@/store/types/mutations';

export default {
  name: 'episode-form',
  components: {
    CoverArtTab,
    MediaTab,
    MetadataTab,
  },
  computed: {
    ...mapGetters({
      account: getters.GET_ACCOUNT,
      episode: getters.GET_EPISODE,
    }),
    defaultImage() {
      const { artwork } = this.episode._embedded;
      if (artwork) return artwork.file.url;
      return null;
    },
    isNew() {
      return this.episode.id === undefined;
    },
    saveBtnLabel() {
      return this.episode.is_published ? 'Publish Episode' : 'Save Episode Draft';
    },
    title() {
      return this.isNew ? 'New Episode' : `Edit Episode: ${this.episode.title}`;
    },
  },
  created() {
    if (this.account.premium_episodes && this.isNew) {
      this.$store.commit(mutations.SET_EPISODE_FIELD, {
        key: 'is_premium',
        val: true,
      });
    }
  },
  data() {
    return {
      active: false,
      activeTab: 0,
      artwork: null,
      error: {
        message: '',
        active: false,
      },
      settings: {
        uploading: 0,
        loading: false,
        submitting: false,
      },
    };
  },
  methods: {
    confirmDelete() {
      this.$buefy.dialog.confirm({
        title: 'Are you sure?',
        message: 'Deleting this episode cannot be undone',
        type: 'is-danger',
        hasIcon: true,
        confirmText: 'Yes, Delete Episode',
        onConfirm: () => this.deleteEpisode(),
      });
    },
    async deleteEpisode() {
      const response = await this.$store.dispatch(actions.DO_DELETE_EPISODE, {
        id: this.episode.id,
      });
      this.$parent.close();
      this.$store.commit(mutations.SET_APP_SNACKBAR, {
        type: 'is-success',
        message: response.message,
      });
      setTimeout(() => {
        this.$store.commit(mutations.SET_MEDIA_REFRESH, true);
      }, 1000);
    },
    async submit() {
      const validate = await this.$validator.validateAll();
      if (validate) {
        this.settings.submitting = true;
        this.error.active = false;
        const action = this.isNew ? actions.DO_CREATE_EPISODE : actions.DO_UPDATE_EPISODE;
        const params = {
          id: this.episode.id,
          title: this.episode.title,
          sub_title: this.episode.sub_title,
          short_desc: this.episode.short_desc,
          long_desc: this.episode.long_desc,
          is_premium: this.episode.is_premium,
          is_published: this.episode.is_published,
          pub_date: moment(this.episode.pub_date).format('YYYY-MM-DD'),
          speaker_id: this.episode.speaker_id,
          audio_id: this.episode._embedded.audio.id,
          video_id: this.episode._embedded.video.id,
          zoom: 'audio',
        };
        const joinFields = [
          'channel_ids',
          'collection_ids',
          'tag_ids',
        ];
        joinFields.forEach((field) => {
          if (_.isArray(this.episode[field]) && this.episode[field].length > 0) {
            params[field] = this.episode[field].join(',');
          }
        });
        if (this.artwork) {
          const file = this.artwork.generateDataUrl('image/jpeg');
          const { data } = await this.$store.dispatch(actions.DO_CREATE_MEDIA_IMAGE, { file });
          params.artwork_id = data.images[0].id;
        }
        if (this.episode._embedded.video.video_link && !this.episode._embedded.video.id) {
          const { data } = await this.$store.dispatch(actions.DO_CREATE_MEDIA_VIDEO, {
            video_id: this.episode._embedded.video.video_id,
            video_link: this.episode._embedded.video.video_link,
            service: this.episode._embedded.video.service,
          });
          params.video_id = data.videos[0].id;
        }
        try {
          const response = await this.$store.dispatch(action, params);
          this.$parent.close();
          this.$store.commit(mutations.SET_APP_SNACKBAR, {
            type: 'is-success',
            message: response.message,
          });
          this.settings.submitting = false;
          setTimeout(() => {
            this.$store.commit(mutations.SET_MEDIA_REFRESH, true);
          }, 1000);
        } catch (error) {
          this.settings.submitting = false;
          this.$store.commit(mutations.SET_APP_SNACKBAR, {
            type: 'is-danger',
            message: error.response.data.data.error_message,
          });
          this.error.active = true;
        }
      }
    },
    trigger() {
      this.active = true;
    },
  },
};
</script>

<style lang="scss">
.upload .upload-draggable {
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/form/EpisodeForm.vue